import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { Seo } from '../../../../components/seo.js';
import { useDebouncedValue } from '../../lib/use-debounced-value.js';
import { ProductListing } from '../components/product-listing.js';

/**
 * Search is exclusively the `search` query param on `GET /catalog/products`
 * (Postgres full-text search) -- there is no dedicated search endpoint and
 * no Meilisearch dependency. This page is the same listing UI as category
 * browsing with `search` set from `?q=` instead of a `categoryId`. The
 * input is debounced before it reaches the URL/query so typing doesn't
 * fire a request per keystroke, but the URL (not just local state) is
 * still the source of truth -- back/forward and bookmarking both work.
 */
export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const [input, setInput] = useState(query);
  const debounced = useDebouncedValue(input, 300);

  // Keep local input in sync if the URL changes from outside this page
  // (e.g. browser back/forward, or a header search-bar submit).
  useEffect(() => {
    setInput(query);
  }, [query]);

  useEffect(() => {
    if (debounced === query) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (debounced.trim()) next.set('q', debounced.trim());
      else next.delete('q');
      next.delete('page');
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <Seo
        title={query ? `Search results for "${query}"` : 'Search'}
        canonicalPath={ROUTE_NAMES.search}
        noindex
      />

      <div className="mb-6">
        <label htmlFor="search-page-input" className="sr-only">
          Search products
        </label>
        <div className="relative max-w-xl">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            id="search-page-input"
            type="search"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="Search for groceries, brands and more"
            className="focus-visible:ring-brand w-full rounded-full border border-gray-200 bg-gray-50 py-2.5 pl-9 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
        <h1 className="mt-4 text-2xl font-bold text-gray-900">
          {query ? (
            <>
              Search results for <span className="text-brand-foreground">&quot;{query}&quot;</span>
            </>
          ) : (
            'Search'
          )}
        </h1>
      </div>

      {query ? (
        <ProductListing
          baseParams={{ search: query }}
          emptyStateDescription={`We couldn't find anything for "${query}". Try a different search.`}
        />
      ) : (
        <p className="text-sm text-gray-500">Start typing to search our catalog.</p>
      )}
    </div>
  );
}
